import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { I18nContent, makeInternationalized } from '../../../../../adl-gen/totalinteraction/futurefit/db';
import { LangCode, withI18n, WithI18nProps } from '../../../../../internationalization/i18n-hoc';
import { PlusIcon } from '../../../../../ui/common/icons';
import { Button } from '../../../../../ui/widgets/button/button';
import {
  // Select,
  SelectBox,
  SelectOption
} from '../../../../../ui/widgets/select/select';
import { FormValue, newNonEmptyTextField, withValueCallback } from '../../../../../ui/widgets/text-input/text-field';
import {
  uniqueId
  // WithId
} from '../../../../../util/utils';
import { AnswerType, I18nInput, I18nTextInputObserver, InputAnswer } from '../../../common/editor';
// import { DndProvider } from "react-dnd"
// import HTML5Backend from "react-dnd-html5-backend"
import { DnDContainer, DraggableEditorOption } from '../../drag-and-drop/drag-and-drop-container';

const styles = require('../../../common/editor.css');
const fonts = require('../../../../../ui/common/fonts.css');

export interface AnswersEditorProps extends WithI18nProps {
  debug?: boolean;
  lang?: LangCode;
  name: string;
  content: I18nContent;
  answers?: InputAnswer<I18nContent>[];
  answerType: AnswerType;

  postUpdates(answers: InputAnswer<I18nContent>[]): void;

  onCancel(): void;
}

/**
 * Base component to create/update answers.
 */
@observer
class AnswersEditor0 extends React.Component<AnswersEditorProps> {
  @observable inputAnswers: InputAnswer<I18nInput>[] = [];
  @observable order: number[] = [];

  @observable lang: LangCode;

  constructor(props: Readonly<AnswersEditorProps>) {
    super(props);
    this.lang = props.lang ? props.lang : props.currentLangCode();
    const { initI18nFromInput, answers } = props;

    if (answers) {
      answers.map(ans => {
        const inputAnswer = makeInternationalized({
          en: withValueCallback(newNonEmptyTextField(), this.onChangeAnswerText)
        });

        this.inputAnswers.push({
          isNotApplicableAnswer: ans.isNotApplicableAnswer,
          answer: {
            id: ans.answer.id,
            value: inputAnswer
          }
        });
        this.order.push(ans.answer.id);
      });
      /// Now initialize - this triggers onSave so it's important the structure is
      /// complete before we initialize
      answers.map(ans => {
        const inputAnswer = this.inputAnswers.find(inputAns => inputAns.answer.id === ans.answer.id);
        if (inputAnswer) {
          /// Should be of course
          initI18nFromInput(inputAnswer.answer.value, ans.answer.value, this.onChangeAnswerText);
        }
      });
    }
  }

  @action
  changeLang = (opt: SelectOption) => {
    this.lang = opt.value as LangCode;
  };

  @action
  onChangeAnswerText = (_ansText: FormValue<string>) => {
    /// Post
    this.onSave();
  };

  LangSelector = observer(() => {
    const { i18nOptions } = this.props;
    const onChange = this.changeLang;
    const options = i18nOptions();
    return (
      <SelectBox
        options={options}
        defaultValue={options.find(option => (option.value as LangCode) === this.lang)}
        onChange={onChange}
        title={'Language'}
        xsSpan={2}
      />
    );
  });

  AnswerTextObserver = observer((props: { answer: I18nInput; idx: number }) => {
    return I18nTextInputObserver({
      field: props.answer,
      type: 'textarea',
      lang: this.lang,
      title: `Answer ${props.idx + 1}`,
      placeholder: `Answer text`,
      xsSpan: 4,
      textAreaRows: 1,
      textAreaCols: 50,
      debug: this.props.debug
    });
  });

  @computed
  get inputIsValid(): boolean {
    let isValid: boolean = true;

    // if (this.answers.length > 0) {
    //   isValid = this.answers.every(answer => this.props.isI18nValid(answer.value))
    // }
    return isValid;
  }

  @action
  onSave = () => {
    const { makeI18nContent, postUpdates } = this.props;
    if (this.inputIsValid) {
      let reorderedAnswers: InputAnswer<I18nContent>[] = this.inputAnswers.map(ans => ({
        isNotApplicableAnswer: ans.isNotApplicableAnswer,
        answer: {
          id: ans.answer.id,
          value: makeI18nContent(ans.answer.value)
        }
      }));
      if (this.order.length > 0) {
        reorderedAnswers = this.order.reduce((acc: InputAnswer<I18nContent>[], pos) => {
          const matchedAns = this.inputAnswers.find(ans => ans.answer.id === pos);
          if (matchedAns) {
            acc.push({
              isNotApplicableAnswer: matchedAns.isNotApplicableAnswer,
              answer: {
                id: matchedAns.answer.id,
                value: makeI18nContent(matchedAns.answer.value)
              }
            });
          }
          return acc;
        }, []);
      }

      postUpdates(reorderedAnswers);
    }
  };

  @action
  onAddAnswer = () => {
    const { initI18nEmpty } = this.props;
    const newId = uniqueId();
    const newAnswerText = makeInternationalized({
      en: withValueCallback(newNonEmptyTextField(), this.onChangeAnswerText)
    });
    initI18nEmpty(newAnswerText, this.onChangeAnswerText);
    const newAnswer = { isNotApplicableAnswer: false, answer: { id: newId, value: newAnswerText } };
    this.inputAnswers.push(newAnswer);
    this.order.push(newAnswer.answer.id);
    this.onSave();
  };

  @action
  onAddNotApplicableAnswer = () => {
    const { initI18nFromInput } = this.props;
    const initContent: I18nContent = {
      kind: 'text',
      value: makeInternationalized({ en: 'This is not an activity I do/not part of my role' })
    };
    const newId = uniqueId();
    const newAnswerText = makeInternationalized({
      en: withValueCallback(newNonEmptyTextField(), this.onChangeAnswerText)
    });
    // initI18nFromInput(newAnswerText, initContent, this.onChangeAnswerText)
    initI18nFromInput(newAnswerText, initContent);
    const newAnswer = { isNotApplicableAnswer: true, answer: { id: newId, value: newAnswerText } };
    this.inputAnswers.push(newAnswer);
    this.order.push(newAnswer.answer.id);
    this.onSave();
  };

  @action
  onRemoveAnswer = (answerId: number) => {
    const ansIndex = this.inputAnswers.findIndex(ans => ans.answer.id === answerId);
    const orderIndex = this.order.indexOf(answerId);
    if (ansIndex >= 0 && orderIndex >= 0) {
      this.inputAnswers.splice(ansIndex, 1);
      this.order.splice(orderIndex, 1);
    }
    this.onSave();
  };

  @action
  onReorder = (order: number[]) => {
    this.order = order.slice();
    this.onSave();
  };

  AddButton = () => {
    return (
      <div className={styles.buttonList}>
        <Button
          // className={styles.button}
          onClick={async () => this.onAddAnswer()}
          Icon={PlusIcon}
          size="small"
          variant="round"
          // disabled={}
        >
          Add
        </Button>
      </div>
    );
  };

  AddNotApplicableResponseButton = () => {
    return (
      <div className={styles.buttonList}>
        <Button
          // className={styles.button}
          onClick={async () => this.onAddNotApplicableAnswer()}
          Icon={PlusIcon}
          size="small"
          variant="round"
          // disabled={}
        >
          Add 'Not Applicable'
        </Button>
      </div>
    );
  };

  OrderedAnswers = observer(
    (): JSX.Element => {
      // tslint:disable-next-line: prefer-const
      let draggableAnswers: DraggableEditorOption[] = this.inputAnswers.map((ans, i) => {
        return {
          id: this.order[i],
          element: <this.AnswerTextObserver answer={ans.answer.value} idx={i} />,
          control: <Button size="small" children="Delete" onClick={async () => this.onRemoveAnswer(ans.answer.id)} />
        };
      });
      return (
        <DnDContainer draggableOptions={draggableAnswers} onMove={opt => this.onReorder(opt.map(item => item.id))} />
      );
    }
  );

  Edit = () => {
    const { content, t } = this.props;
    return (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            <this.LangSelector />
          </div>
          <div className={styles.row}>
            <span className={fonts.caption}>{t(content, this.lang)}</span>
          </div>
          <this.OrderedAnswers />
        </div>
      </div>
    );
  };

  render() {
    const { answerType, name } = this.props;
    const title = `Answers for question ${name} (${answerType})`;
    return (
      <React.Fragment>
        <div>{title}</div>
        <div>
          <this.AddNotApplicableResponseButton />
          <this.AddButton />
        </div>
        <this.Edit />
      </React.Fragment>
    );
  }
}

export const AnswersEditor = withI18n(AnswersEditor0);



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/answers-scoring-editors/answers-editor.tsx